// Structured data for the data pages (2 Nov 2026), one rule for the live pages (Company.tsx, Dataset.tsx,
// Funds.tsx) and the pages scripts/company-pages.mjs writes for crawlers that do not run JavaScript, so a
// search engine or an AI assistant reads the same facts either way. Every value comes from the page's own file.
const SITE = "https://shikshannivesh.com";
const ORG = { "@type": "Organization", "@id": `${SITE}/#organization`, name: "Shikshan Nivesh", url: SITE };
const LICENCE = `${SITE}/data/licence`;
const ctx = (o) => ({ "@context": "https://schema.org", ...o });

/** [[name, path], ...] from the top, the last one the page itself */
export const crumbsLd = (items) => ctx({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE}${path}` })),
});

export const faqLd = (faqs) => (faqs && faqs.length ? ctx({
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
}) : null);

/** a company page: the company itself, with its ticker, exchange and SEC number */
export const companyLd = (id, path) => {
  const cik = id.cik ? String(id.cik) : null;
  const ids = [cik && { "@type": "PropertyValue", propertyID: "CIK", value: cik },
    id.ids?.lei && { "@type": "PropertyValue", propertyID: "LEI", value: id.ids.lei },
    id.ids?.figi && { "@type": "PropertyValue", propertyID: "FIGI", value: id.ids.figi }].filter(Boolean);
  return ctx({
    "@type": "Corporation",
    "@id": `${SITE}${path}#company`,
    name: id.name,
    tickerSymbol: id.symbol,
    url: `${SITE}${path}`,
    ...(id.ids?.lei ? { leiCode: id.ids.lei } : {}),
    ...(ids.length ? { identifier: ids } : {}),
    ...(id.industry ? { description: `${id.name} (${id.symbol}), ${String(id.industry).replace(/\s+/g, " ").trim().toLowerCase()}${id.exchange ? `, listed on ${id.exchange}` : ""}.` } : {}),
    ...(cik ? { sameAs: [`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${cik.padStart(10, "0")}`] } : {}),
  });
};

/** a dataset page: what it holds, where it comes from, the licence and the years it covers */
export const datasetLd = ({ name, description, path, source, from, modified, keywords }) => ctx({
  "@type": "Dataset",
  name,
  description,
  url: `${SITE}${path}`,
  license: LICENCE,
  isAccessibleForFree: true,
  creator: ORG,
  publisher: ORG,
  spatialCoverage: "United States",
  ...(source ? { isBasedOn: source } : {}),
  ...(from ? { temporalCoverage: `${from.slice(0, 10)}/..` } : {}),
  ...(modified ? { dateModified: modified.slice(0, 10) } : {}),
  ...(keywords ? { keywords } : {}),
});

/** a fund page: the fund's latest 13F holdings as a dataset about the manager that filed it */
export const fundLd = (fund, path, quarter) => ctx({
  "@type": "Dataset",
  name: `${fund.name} 13F holdings, ${quarter}`,
  description: `Every holding ${fund.name} reported on its Form 13F for the ${quarter}, with what it opened, added, trimmed and sold.`,
  url: `${SITE}${path}`,
  license: LICENCE,
  isAccessibleForFree: true,
  creator: ORG,
  isBasedOn: fund.url || "Form 13F filings with the SEC",
  ...(fund.period ? { temporalCoverage: fund.period } : {}),
  ...(fund.filed ? { dateModified: fund.filed } : {}),
  about: {
    "@type": "Organization",
    name: fund.name,
    identifier: { "@type": "PropertyValue", propertyID: "CIK", value: String(fund.cik) },
    sameAs: [`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${String(fund.cik).padStart(10, "0")}`],
  },
});

/** a list page (every company, every fund): a collection inside the site */
export const collectionLd = ({ name, description, path }) => ctx({ "@type": "CollectionPage", name, description, url: `${SITE}${path}`, isPartOf: { "@id": `${SITE}/#website` }, publisher: ORG });
